import React, { useEffect, useRef, useState } from 'react';
import {
    arrayOf,
    bool,
    func,
    number,
    object,
    oneOf,
    oneOfType,
    shape,
    string
} from 'prop-types';
import clsx from 'clsx';
import Downshift from 'downshift';
import { isObject } from 'lodash';

import AutocompleteInput from './AutocompleteInput';
import SuggestionList from './SuggestionList';

import defaultClasses from './Autocomplete.module.css';

function defaultItemToKey(item, index) {
    return item.id || index;
}

function defaultItemToString(item) {
    if (!item) {
        return '';
    }
    if (item.length) {
        return item || '';
    }
    return item.name || item.label || '';
}

/**
 * Returns TRUE if the item is already selected. A custom implementation of this
 * can be provided via the `isItemSelected` prop if a property other than `id`
 * is required to track the selection state.
 */
function isItemSelected(item, selectedItems) {
    if (isObject(item)) {
        return selectedItems.some(selectedItem => item.id === selectedItem.id);
    }
    return selectedItems.includes(item);
}

const Autocomplete = ({
    adornmentPosition,
    AdornmentProps,
    classes,
    className,
    overrideClass,
    overrideErrorClass,
    overrideLabelClass,
    disabled,
    error,
    grouped,
    helperText,
    hidden,
    hideSelected,
    InputProps,
    isItemSelected,
    itemToKey,
    itemToString,
    label,
    loading,
    maxSuggestions,
    multiple,
    name,
    onBlur,
    onChange,
    onKeyDown,
    options,
    value,
    ...props
}) => {
    const [searchRan, setSearchRan] = useState(false);
    const [groupedOptions, setGroupedOptions] = useState([]);
    const [selectedItem, setSelectedItem] = useState(() => {
        return value || (multiple ? [] : '');
    });
    const [inputValue, setInputValue] = useState(() => {
        return multiple ? '' : itemToString(selectedItem);
    });
    const popperNode = useRef(null);

    useEffect(() => {
        if (grouped) {
            setGroupedOptions(options);
            return;
        }
        setGroupedOptions([
            {
                items: options
            }
        ]);
    }, [options]);

    useEffect(() => {
        const newValue = value || (multiple ? [] : '');
        setSelectedItem(newValue);
        setInputValue(multiple ? '' : itemToString(newValue));
    }, [multiple, value]);

    function handleChange(newValue) {
        if (!multiple) {
            onChange({
                target: {
                    name,
                    value: newValue
                }
            });
            return;
        }
        if (!newValue) {
            return;
        }
        const newValues = [...selectedItem];
        if (!isItemSelected(newValue, selectedItem)) {
            newValues.push(newValue);
        }
        onChange({
            target: {
                name,
                value: newValues
            }
        });
    }

    function handleBlur(event) {
        if (multiple) {
            setInputValue('');
            onBlur(event);
            return;
        }
        if (!inputValue) {
            onBlur(event);
            return;
        }
        setInputValue(itemToString(selectedItem));
        onBlur(event);
    }

    function handleKeyDown(event) {
        switch (event.key) {
            case 'Backspace':
                if (selectedItem.length && !inputValue.length) {
                    setSelectedItem(selectedItem.slice(0, -1));
                }

                // If component was given a function to fire on the onKeyDown prop, then fire it
                if (onKeyDown) onKeyDown();

                // "searchRan" is a flag to indicate whether we need to show the "Hit Enter..."
                // message
                setSearchRan(false);

                break;
            case 'Enter':
                // If component was given a function to fire on the onKeyDown prop, then fire it
                // with the current input value
                if (onKeyDown) onKeyDown(inputValue);

                // "searchRan" is a flag to indicate whether we need to show the "Hit Enter..."
                // message
                setSearchRan(true);

                event.preventDefault();
                break;
            default:
                return;
        }
    }

    function handleInputChange(event) {
        const { value } = event.target;

        setInputValue(value);

        // If value is empty and there are no selected items fire the onChange to trigger
        // a formik validation in components that use this component (outside of TagSelect).
        if (!value && !selectedItem.length) {
            onChange({
                target: {
                    name,
                    value
                }
            });
        }
    }

    function handleDelete(item) {
        if (!multiple) {
            return;
        }
        const newSelectedItem = [...selectedItem];
        const itemIndex = selectedItem.indexOf(item);
        if (itemIndex === -1) {
            throw new Error('Could not find deleted item in selected items');
        }
        newSelectedItem.splice(itemIndex, 1);
        onChange({
            target: {
                name,
                value: newSelectedItem
            }
        });
    }

    if (hidden) {
        return null;
    }

    return (
        <Downshift
            inputValue={inputValue}
            itemToString={itemToString}
            selectedItem={selectedItem}
            onChange={handleChange}
            {...props}
        >
            {({
                getInputProps,
                getItemProps,
                getMenuProps,
                highlightedIndex,
                inputValue: downshiftInputValue,
                isOpen,
                itemToString: downshiftItemToString,
                selectedItem: downshiftSelectedItem,
                toggleMenu
            }) => (
                <div
                    className={clsx(
                        defaultClasses.root,
                        classes.root,
                        className,
                        {
                            [classes.error]: error
                        }
                    )}
                >
                    <AutocompleteInput
                        adornmentPosition={adornmentPosition}
                        AdornmentProps={AdornmentProps}
                        classes={classes}
                        overrideClass={overrideClass}
                        overrideLabelClass={overrideLabelClass}
                        overrideErrorClass={overrideErrorClass}
                        disabled={disabled}
                        error={error}
                        helperText={helperText}
                        InputProps={getInputProps({
                            ...props,
                            ...InputProps,
                            onBlur:    handleBlur,
                            onKeyDown: handleKeyDown,
                            onChange:  handleInputChange
                        })}
                        itemToString={downshiftItemToString}
                        label={label}
                        multiple={multiple}
                        name={name}
                        onClick={toggleMenu}
                        onDelete={handleDelete}
                        ref={popperNode}
                        selectedItem={selectedItem}
                    />
                    <SuggestionList
                        {...getMenuProps()}
                        classes={classes}
                        getItemProps={getItemProps}
                        groupedOptions={groupedOptions}
                        hideSelected={hideSelected}
                        highlightedIndex={highlightedIndex}
                        inputValue={downshiftInputValue}
                        isOpen={isOpen}
                        itemToKey={defaultItemToKey}
                        itemToString={downshiftItemToString}
                        maxSuggestions={maxSuggestions}
                        popperNode={popperNode}
                        selectedItem={downshiftSelectedItem}
                        onKeyDown={onKeyDown}
                        searchRan={searchRan}
                        loading={loading}
                    />
                </div>
            )}
        </Downshift>
    );
};

Autocomplete.defaultProps = {
    adornmentPosition: 'start',
    AdornmentProps:    {
        classes: {}
    },
    classes:        {},
    error:          false,
    grouped:        false,
    hidden:         false,
    hideSelected:   false,
    InputProps:     {},
    isItemSelected: isItemSelected,
    itemToKey:      defaultItemToKey,
    itemToString:   defaultItemToString,
    multiple:       false,
    onBlur:         () => {},
    onChange:       () => {},
    options:        []
};

Autocomplete.props = {
    adornmentPosition: oneOf(['start', 'end']),
    AdornmentProps:    object,
    classes:           object,
    className:         string,
    error:             bool,
    grouped:           bool,
    helperText:        string,
    hidden:            bool,
    hideSelected:      bool,
    InputProps:        object,
    isItemSelected:    func,
    itemToKey:         func,
    itemToString:      func,
    label:             string,
    maxSuggestions:    number,
    multiple:          bool,
    onBlur:            func,
    onChange:          func,
    options:           oneOfType([
        arrayOf(oneOfType([number, object, string])),
        arrayOf(shape({
            isLoading: bool,
            items:     oneOfType([number, object, string]),
            name:      string,
            type:      string
        }))
    ]),
    value: oneOfType([number, object, string])
};

export default Autocomplete;
